/**
 * Returns whether a number is valid and therefore it can be parsed.
 * For example "12.1" is a valid number but "12,1" is not.
 *
 * @param value The value to analyze can be of any type but only
 * number or string can return true.
 * @returns True if the provided number is valid.
 */
export function isValidNumber(value) {
  if (typeof value === "number") {
    return true;
  }
  if (typeof value === "string") {
    return /^-?\d+(\.\d+)?$/.test(value);
  }
  return false;
}

/**
 * Base response for validateNumber.
 */

/**
 * Validates that a number is valid and that meets certain criteria.
 * If it is valid, returns true and the parsed number.
 * If it is invalid returns false and en error message.
 */
export function validateNumber({
  value,
  attrName,
  shouldAllowDecimal = true,
  min,
  minOrEqual,
  max,
  maxOrEqual
}) {
  let parsedNumber;
  const errorMessageInvalidNumber = `${attrName} must be a valid number.`;
  if (typeof value === "number") {
    parsedNumber = value;
  } else if (typeof value === "string") {
    const str = value.replace(",", ".");
    if (!isValidNumber(str)) {
      return {
        isValid: false,
        errorMessage: errorMessageInvalidNumber
      };
    }
    parsedNumber = parseFloat(str);
    if (isNaN(parsedNumber)) {
      return {
        isValid: false,
        errorMessage: errorMessageInvalidNumber
      };
    }
  } else {
    return {
      isValid: false,
      errorMessage: errorMessageInvalidNumber
    };
  }
  if (min === undefined && minOrEqual === undefined && max === undefined && maxOrEqual === undefined && shouldAllowDecimal) {
    return {
      isValid: true,
      parsedNumber
    };
  }
  if (!shouldAllowDecimal && !Number.isInteger(parsedNumber)) {
    return {
      isValid: false,
      errorMessage: `${attrName} cannot be a decimal number.`
    };
  }
  if (min !== undefined && parsedNumber <= min) {
    return {
      isValid: false,
      errorMessage: `${attrName} must be greater than ${min}.`
    };
  }
  if (minOrEqual !== undefined && parsedNumber < minOrEqual) {
    const errorMessage = parsedNumber < 0 && minOrEqual >= 0 ? `${attrName} cannot be a negative value.` : `${attrName} must be greater or equal than ${minOrEqual}.`;
    return {
      isValid: false,
      errorMessage
    };
  }
  if (max !== undefined && parsedNumber >= max) {
    return {
      isValid: false,
      errorMessage: `${attrName} must be lower than ${max}.`
    };
  }
  if (maxOrEqual !== undefined && parsedNumber > maxOrEqual) {
    return {
      isValid: false,
      errorMessage: `${attrName} must be lower or equal than ${maxOrEqual}.`
    };
  }
  return {
    isValid: true,
    parsedNumber
  };
}